/**
 * Voxel coordinate utilities — T-1.2.2.
 *
 * Pure functions over `VoxelCoord` (readonly integer triples).
 *
 * **No side effects, no store, no Three.js.** This module is the contract
 * layer that every higher-level geometry command (T-1.3.x place/move/...)
 * and the six-direction projection (T-3.1) will rely on.
 *
 * ## Rounding convention (`snapToGrid`)
 *
 * We delegate to `Math.round`, whose ECMAScript semantics are
 * "round half toward positive infinity" (`-0.5 → 0`, `0.5 → 1`).
 * This is the natural fit for voxel snapping — it picks the cell
 * whose integer center is closest along the axis, and it does so
 * deterministically regardless of sign. Callers that need
 * banker's rounding must implement it on top.
 *
 * Source of truth: SPEC §2.1 (VoxelCoord) + T-1.2.2 task card §1.
 */

import type { VoxelCoord } from '@/state/types';

/** Snap a world-space point to the nearest integer voxel coordinate. */
export function snapToGrid(p: readonly [number, number, number]): VoxelCoord {
  // Note: Math.round(-0.5) === -0 in JS (IEEE-754). VoxelCoord is a logical
  // integer index, so we collapse signed-zero to +0 to keep keys stable
  // and avoid `Object.is` / `toEqual` false-negatives downstream.
  const norm = (n: number) => (n === 0 ? 0 : n);
  return [norm(Math.round(p[0])), norm(Math.round(p[1])), norm(Math.round(p[2]))];
}

/** Encode a voxel coord as `"x,y,z"` (canonical key for Maps / React lists). */
export function coordKey(c: VoxelCoord): string {
  return `${c[0]},${c[1]},${c[2]}`;
}

/** Inverse of `coordKey`. Throws `RangeError` on malformed input. */
export function parseCoordKey(k: string): VoxelCoord {
  const parts = k.split(',');
  if (parts.length !== 3) {
    throw new RangeError(`coordKey must have exactly 3 comma-separated parts: ${k}`);
  }
  const x = Number(parts[0]);
  const y = Number(parts[1]);
  const z = Number(parts[2]);
  if (!Number.isInteger(x) || !Number.isInteger(y) || !Number.isInteger(z)) {
    throw new RangeError(`coordKey parts must be integers: ${k}`);
  }
  return [x, y, z];
}

/**
 * The 6 axis-aligned face-adjacent neighbors of a coord.
 *
 * Order is deterministic: +x, -x, +y, -y, +z, -z. This order matters for
 * deterministic command traversal (T-1.3.x raycast walks neighbors in
 * fixed order to avoid selection flicker).
 */
export function neighbors(c: VoxelCoord): VoxelCoord[] {
  const [x, y, z] = c;
  return [
    [x + 1, y, z],
    [x - 1, y, z],
    [x, y + 1, z],
    [x, y - 1, z],
    [x, y, z + 1],
    [x, y, z - 1],
  ];
}

/**
 * Face-adjacency test — true iff `a` and `b` differ by exactly one unit
 * along exactly one axis (Manhattan distance 1). Edge- and corner-adjacent
 * pairs return false; the same coord returns false.
 */
export function isAdjacent(a: VoxelCoord, b: VoxelCoord): boolean {
  return manhattanDistance(a, b) === 1;
}

/** Sum of absolute axis deltas. Always non-negative, integer-valued. */
export function manhattanDistance(a: VoxelCoord, b: VoxelCoord): number {
  return Math.abs(a[0] - b[0]) + Math.abs(a[1] - b[1]) + Math.abs(a[2] - b[2]);
}

/**
 * Axis-aligned bounding box of a voxel map.
 *
 * Returns `null` for an empty map (no bbox defined). Returns
 * `{ min, max }` where `min` and `max` are the per-axis extrema
 * (component-wise min/max, not necessarily two voxels in the set).
 *
 * Generic over the value type so callers can use either
 * `Map<VoxelCoord, VoxelProps>` or `Map<VoxelCoord, string>` etc.
 */
export function bbox<V>(voxels: Map<VoxelCoord, V>): { min: VoxelCoord; max: VoxelCoord } | null {
  if (voxels.size === 0) return null;

  let minX = Infinity;
  let minY = Infinity;
  let minZ = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  let maxZ = -Infinity;

  for (const [x, y, z] of voxels.keys()) {
    if (x < minX) minX = x;
    if (x > maxX) maxX = x;
    if (y < minY) minY = y;
    if (y > maxY) maxY = y;
    if (z < minZ) minZ = z;
    if (z > maxZ) maxZ = z;
  }

  return {
    min: [minX, minY, minZ],
    max: [maxX, maxY, maxZ],
  };
}
